import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Button } from 'react-bootstrap';
import { RiImageLine } from 'react-icons/ri';
import { supabase } from '../../supabase/supabase';
import ImageSkeleton from '../ImageViewer/ImageSkeleton';
import LoadingLogo from '../LoadingLogo';

// Load CSS
import 'react-photo-album/rows.css';
import 'yet-another-react-lightbox/styles.css';
import '../ImageViewer/style.less';

// Lazy load heavy components
const RowsPhotoAlbum = lazy(() =>
    import('react-photo-album').then((module) => ({ default: module.RowsPhotoAlbum }))
);
const Lightbox = lazy(() => import('yet-another-react-lightbox'));

// Import Captions plugin
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/captions.css';
import { getMissionaryImages } from '../../api';

interface ImageData {
    attachment_id: number;
    original_filename: string;
    file_type: string;
    file_size: number;
    upload_date: string;
    url: string;
    preview_url?: string;
    email_id: string;
}

interface MissionaryImageData {
    missionary_name: string;
    missionary_email: string;
    images: ImageData[];
}

interface MissionaryImageViewerProps {
    uniqueId: string;
}

const MissionaryImageViewer: React.FC<MissionaryImageViewerProps> = ({ uniqueId }) => {
    const [imageData, setImageData] = useState<MissionaryImageData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [photosWithDimensions, setPhotosWithDimensions] = useState<any[]>([]);
    const [showViewer, setShowViewer] = useState(false);
    const [imagesLoading, setImagesLoading] = useState(false);

    useEffect(() => {
        if (showViewer && !imageData) {
            fetchImages();
        }
    }, [showViewer, uniqueId]);

    useEffect(() => {
        if (imageData?.images) {
            loadImageDimensions();
        }
    }, [imageData]);

    const loadImageDimensions = async () => {
        setImagesLoading(true);
        const imageFiles = imageData!.images.filter(
            (image) => !image.file_type.startsWith('video/')
        );
        const photosWithDims = await Promise.all(
            imageFiles.map((image) => {
                return new Promise<any>((resolve) => {
                    const img = new Image();
                    const previewUrl = image.preview_url || image.url;
                    
                    img.onload = () => {
                        resolve({
                            src: previewUrl,
                            width: img.naturalWidth || 400,
                            height: img.naturalHeight || 300,
                            alt: image.original_filename,
                            fullSrc: image.url
                        });
                    };
                    img.onerror = () => {
                        resolve({
                            src: image.url,
                            width: 400,
                            height: 300,
                            alt: image.original_filename,
                            fullSrc: image.url
                        });
                    };
                    img.src = previewUrl;
                });
            })
        );
        setPhotosWithDimensions(photosWithDims);
        setImagesLoading(false);
    };

    const fetchImages = async (pageNum = 1) => {
        setLoading(true);
        try {
            const session = await supabase.auth.getSession();
            if (!session.data.session) {
                throw new Error('Not authenticated');
            }

            const data = await getMissionaryImages(uniqueId, pageNum);

            if (pageNum === 1) {
                setImageData(data);
            } else {
                setImageData((prev) =>
                    prev
                        ? {
                              ...prev,
                              images: [...prev.images, ...data.images]
                          }
                        : data
                );
            }

            setHasMore(data.images.length === 20);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load images');
        } finally {
            setLoading(false);
        }
    };

    const loadMore = () => {
        if (hasMore && !loading) {
            setPage((prev) => prev + 1);
            fetchImages(page + 1);
        }
    };

    const openLightbox = (index: number) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const lightboxSlides =
        imageData?.images
            .filter((image) => !image.file_type.startsWith('video/'))
            .map((image) => ({
                src: image.url,
                alt: image.original_filename,
                description: `${image.original_filename} • ${Math.round(image.file_size / 1024)} KB • ${new Date(image.upload_date).toLocaleDateString()}`
            })) || [];

    if (!showViewer) {
        return (
            <div className="text-center">
                <Button
                    variant="outline-primary"
                    onClick={() => setShowViewer(true)}
                    className="d-flex align-items-center mx-auto"
                >
                    <RiImageLine className="me-2" />
                    View All Images
                </Button>
            </div>
        );
    }

    if (loading && !imageData) {
        return <LoadingLogo />;
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                <h5>Unable to Load Images</h5>
                <p>{error}</p>
            </div>
        );
    }

    if (!imageData) {
        return null;
    }

    return (
        <div className="missionary-image-viewer">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>All Images</h4>
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowViewer(false)}
                >
                    Hide Images
                </Button>
            </div>

            {imageData.images.length === 0 ? (
                <div className="text-center py-4">
                    <h5>No Images Available</h5>
                    <p className="text-muted">No images have been uploaded yet.</p>
                </div>
            ) : (
                <>
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                        {photosWithDimensions.length > 0 && !imagesLoading ? (
                            <Suspense
                                fallback={
                                    <div className="skeleton-grid">
                                        {Array.from({ length: Math.min(imageData.images.length, 8) }).map((_, index) => (
                                            <ImageSkeleton key={index} />
                                        ))}
                                    </div>
                                }
                            >
                                <RowsPhotoAlbum
                                    photos={photosWithDimensions}
                                    spacing={8}
                                    onClick={({ index }) => openLightbox(index)}
                                />
                            </Suspense>
                        ) : (
                            <div className="skeleton-grid">
                                {Array.from({ length: Math.min(imageData.images.length, 8) }).map((_, index) => (
                                    <ImageSkeleton key={index} />
                                ))}
                            </div>
                        )}
                    </div>

                    {hasMore && (
                        <div className="text-center mt-3">
                            <Button
                                variant="outline-primary"
                                onClick={loadMore}
                                disabled={loading}
                            >
                                {loading ? 'Loading...' : 'Load More'}
                            </Button>
                        </div>
                    )}

                    {lightboxOpen && (
                        <Suspense fallback={<div>Loading...</div>}>
                            <Lightbox
                                open={lightboxOpen}
                                close={() => setLightboxOpen(false)}
                                slides={lightboxSlides}
                                index={lightboxIndex}
                                plugins={[Captions]}
                                captions={{
                                    showToggle: true,
                                    descriptionTextAlign: 'start'
                                }}
                            />
                        </Suspense>
                    )}
                </>
            )}
        </div>
    );
};

export default MissionaryImageViewer;